import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Typography, Button, Input } from '@crisellevaldez/speculo-ui';
import { RotateCw, Save, Check, Search } from 'lucide-react';
import { RolloutStage, getStageLabel } from '../../../api/rollout';
import { useRolloutStages } from '../../../hooks/rollout/useRolloutStages';

// Custom checkbox component to avoid click propagation issues
const StageCheckbox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <div
    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
      checked ? 'bg-green-600 border-green-600' : 'bg-white dark:bg-white/5 border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/40'
    }`}
  >
    {checked && <Check className="w-3 h-3 text-white" />}
  </div>
);

interface FubStagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId?: string;
  onSuccess?: () => void;
}

export const FubStagesModal: React.FC<FubStagesModalProps> = ({
  isOpen,
  onClose,
  orgId,
  onSuccess,
}) => {
  // Local state to track user selections (initialized when modal opens)
  const [localStages, setLocalStages] = useState<RolloutStage[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredStages = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return localStages;
    return localStages.filter((s) => getStageLabel(s).toLowerCase().includes(q));
  }, [localStages, searchQuery]);

  // Fetch stages from Rollout
  const {
    stages: rolloutStages,
    isLoading,
    error,
    refetch,
    saveStages,
    isSaving,
  } = useRolloutStages({
    orgId,
    enabled: isOpen,
  });

  // Initialize local stages when data arrives (only once per modal open)
  useEffect(() => {
    if (isOpen && rolloutStages.length > 0 && !hasInitialized) {
      setLocalStages(rolloutStages);
      setHasInitialized(true);
    }
  }, [isOpen, rolloutStages, hasInitialized]);

  // Reset state when modal closes
  const handleClose = () => {
    setLocalStages([]);
    setHasInitialized(false);
    setSearchQuery('');
    onClose();
  };

  // Toggle stage enabled status
  const handleToggleStage = (stageId: RolloutStage['id']) => {
    setLocalStages((prev) =>
      prev.map((s) => (s.id === stageId ? { ...s, enabled: !s.enabled } : s))
    );
  };

  // Save stages
  const handleSave = async () => {
    await saveStages(localStages);
    onSuccess?.();
    handleClose();
  };

  // Calculate stats
  const enabledCount = localStages.filter((s) => s.enabled).length;
  const totalCount = localStages.length;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">Configure FUB Stages</Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Select which Follow Up Boss stages should sync to Speculo AI.
          </p>

          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center gap-2 justify-center py-8 text-gray-600 dark:text-gray-400">
              <RotateCw className="w-5 h-5 animate-spin" />
              <span>Loading stages from Follow Up Boss...</span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4">Failed to load stages</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          )}

          {/* Search */}
          {!isLoading && !error && localStages.length > 0 && (
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search stages..."
              startIcon={<Search className="text-gray-400" size={16} />}
            />
          )}

          {/* Stages list */}
          {!isLoading && !error && filteredStages.length > 0 && (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {filteredStages.map((stage) => (
                <div
                  key={stage.id}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                    stage.enabled
                      ? 'border-green-300 dark:border-green-700/50 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'
                  }`}
                  onClick={() => handleToggleStage(stage.id)}
                >
                  <div className="flex items-center gap-3">
                    <StageCheckbox checked={stage.enabled} />
                    <span
                      className={stage.enabled ? 'font-medium text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}
                    >
                      {getStageLabel(stage)}
                    </span>
                  </div>
                  {typeof stage.peopleCount === 'number' && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">{stage.peopleCount as number} contacts</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* No stages */}
          {!isLoading && !error && localStages.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">No stages found in Follow Up Boss</div>
          )}

          {/* No matching stages (search active) */}
          {!isLoading && !error && localStages.length > 0 && filteredStages.length === 0 && (
            <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
              No stages match &quot;{searchQuery}&quot;
            </div>
          )}

          {/* Summary */}
          {localStages.length > 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
              {enabledCount} of {totalCount} stages selected
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={isSaving} className="h-8">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving || isLoading || localStages.length === 0}
            className="flex items-center gap-2 h-8"
          >
            {isSaving ? (
              <>
                <RotateCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Stages
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
